<template>
  <div class="layout-menu">
    <m-dropdown
      block
      :disabled="layoutDisabled"
      :title="$t('savedViewEditor.layout')"
    >
      <m-card-item
        icon="layout"
        :disabled="layoutDisabled"
      >
        {{ $t('savedViewEditor.layout') }}
        <template #right>
          <div :class="['_layout-type', layoutDisabled ? '-disabled' : '']">
            {{ getViewName(type) }}
          </div>
          <div class="_icon">
            <m-icon
              v-if="!layoutDisabled"
              type="right"
              :color="$colors.grey.lighten1"
              size="11"
            />
          </div>
        </template>
      </m-card-item>
      <template #overlay>
        <m-card
          list
        >
          <m-content
            class="_layout-grid"
            padding-xs
          >
            <div
              v-for="v in allowedViewTypes"
              :key="v"
              class="_layout-button-container"
            >
              <plan-tag
                v-if="v === viewType.tree"
                class="_plan-tag"
                :feature-flag="featureFlag.goalTree"
                :style="{ display: 'inline-flex' }"
              />
              <select-btn
                class="_select-btn"
                :selected="isSelectViewType(v)"
                :clickable="isClickable(v)"
                :disabled="!canEdit"
                :icon="getViewIcon(v)"
                :icon-color="isSelectViewType(v) ? $colors.blue.darken1 : $colors.grey.lighten1"
                @click="type = v"
              >
                {{ getViewName(v) }}
              </select-btn>
            </div>
          </m-content>
          <div>
            <m-card-item
              v-if="displayParentAlignment"
              data-cy="parent-alignment-select"
              @click="showParentAlignmentMenu = true"
            >
              {{ parentAlignmentLabel }}
              <template #right>
                <m-select
                  :open="showParentAlignmentMenu"
                  :items="parentAlignmentOptions"
                  :value="parentAlignment"
                  has-tooltip
                  tooltip-placement="left"
                  hide-border
                  hide-hover
                  light
                  no-padding
                  @update:open="showParentAlignmentMenu = $event"
                  @update:value="updateParentAlignment"
                />
              </template>
            </m-card-item>
            <m-card-item
              v-if="displayWrapCells"
              :clickable="!readOnly"
              :no-hover="readOnly"
              tooltip-placement="left"
              :tooltip="readOnly ? $t('documentListHeader.lockedHint') : ''"
              @click="updateWrapCells"
            >
              <m-switch
                :value="wrapCells"
                small
                :disabled="readOnly"
                :label="$t('documentListHeader.wrapCells')"
              />
            </m-card-item>
            <card-width-selector
              v-if="displayCardWidth"
              :value="cardWidth"
              :disabled="readOnly"
              @update:value="updateCardWidth"
            />
            <m-card-item
              v-if="displayWrapTitles"
              :tooltip="$t('documentListHeader.wrapTitlesTooltip')"
              tooltip-placement="left"
              @click="updateWrapTitles"
            >
              <m-switch
                :value="wrapTitles"
                small
                :disabled="readOnly"
                :label="$t('documentListHeader.wrapTitles')"
              />
            </m-card-item>
            <m-card-item
              v-if="displayLoadLimit"
              @click="showLoadLimitMenu = true"
            >
              {{ loadLimitLabel }}
              <template #right>
                <m-select
                  :open="showLoadLimitMenu"
                  :items="loadLimitOptions"
                  :value="loadLimit"
                  hide-border
                  hide-hover
                  light
                  no-padding
                  @update:open="showLoadLimitMenu = $event"
                  @update:value="updateLoadLimit"
                />
              </template>
            </m-card-item>
          </div>
        </m-card>
      </template>
    </m-dropdown>
  </div>
</template>

<script>
import CardWidthSelector from '@/components/list/CardWidthSelector.vue';
import PlanTag from '@/components/plans/PlanTag.vue';
import SelectBtn from '@/components/SelectBtn.vue';
import useAccess from '@/composables/access/access';
import useDebounce from 'shared/composables/debounce';
import useLoadLimit from '@/composables/load-limit';
import useParentAlignment from '@/composables/parent-alignment';
import { CARD_SIZE_MEDIUM } from '@/lib/constants';
import { computed, toRef } from 'vue';
import { featureFlag, viewType } from 'shared/constants.json';
import { iconByType } from '@/lib/saved-view/saved-view';

export default {
  name: 'LayoutMenu',
  props: {
    savedView: {
      type: Object,
      required: true,
    },
    allowedViewTypes: {
      type: Array,
      default: () => [viewType.default],
    },
    viewApplication: {
      type: String,
      default: '',
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update', 'update-view-params'],
  components: { PlanTag, CardWidthSelector, SelectBtn },
  setup(props) {
    const { accountHasFeature } = useAccess();
    const { debounce } = useDebounce({ onBeforeUnmountFlush: true });
    const { loadLimitOptions, loadLimit, loadLimitLabel } = useLoadLimit(toRef(props, 'savedView'));
    const { parentAlignmentOptions, parentAlignmentLabel, parentAlignment } = useParentAlignment(toRef(props, 'savedView'));

    const cardWidth = computed(() => props.savedView.params?.cardWidth || CARD_SIZE_MEDIUM);
    const wrapCells = computed(() => props.savedView.params?.wrapCells || false);
    const wrapTitles = computed(() => props.savedView.params?.wrapTitles || false);

    return {
      debounce,
      featureFlag,
      accountHasFeature,
      loadLimitOptions,
      loadLimit,
      loadLimitLabel,
      parentAlignmentOptions,
      parentAlignmentLabel,
      parentAlignment,
      cardWidth,
      wrapCells,
      wrapTitles,
    };
  },
  data() {
    return {
      type: null,
      viewType,
      showLoadLimitMenu: false,
      showParentAlignmentMenu: false,
    };
  },
  computed: {
    canActivateGoalTree() {
      return this.accountHasFeature([featureFlag.goalTree]);
    },
    displayParentAlignment() {
      return this.type === viewType.cascade || this.type === viewType.tree;
    },
    displayWrapCells() {
      return this.type === viewType.cascade;
    },
    displayCardWidth() {
      return this.type === viewType.tree || this.type === viewType.atlas;
    },
    displayWrapTitles() {
      return this.type === viewType.feed;
    },
    displayLoadLimit() {
      return this.type !== viewType.default;
    },
    layoutDisabled() {
      return this.allowedViewTypes.length === 1 && !(this.displayParentAlignment || this.displayWrapCells || this.displayCardWidth || this.displayWrapTitles);
    },
  },
  methods: {
    getViewIcon(value) {
      return iconByType(value);
    },
    getViewName(value) {
      return this.$t(`list.${value}`);
    },
    emitUpdate() {
      const view = {
        uid: this.savedView.uid,
        isTemp: this.savedView.isTemp,
        viewType: this.type,
      };

      this.$emit('update', view);
    },
    updateDebounced(wait = 0) {
      const fn = () => this.emitUpdate();
      this.debounce(fn, wait);
    },
    isSelectViewType(value) {
      return value === this.type;
    },
    isClickable(value) {
      return !(this.isSelectViewType(value) || (value === viewType.tree && !this.canActivateGoalTree));
    },
    updateCardWidth(value) {
      this.$emit('update-view-params', value, 'cardWidth');
    },
    updateWrapCells() {
      this.$emit('update-view-params', !this.wrapCells, 'wrapCells');
    },
    updateParentAlignment(value) {
      this.$emit('update-view-params', value, 'parentAlignment');
    },
    updateWrapTitles() {
      this.$emit('update-view-params', !this.wrapTitles, 'wrapTitles');
    },
    updateLoadLimit(value) {
      this.$emit('update-view-params', value, 'loadLimit');
    },
  },
  watch: {
    type(newVal, oldVal) {
      if (newVal === oldVal || oldVal === null) {
        return;
      }
      this.updateDebounced();
    },
  },
  created() {
    this.type = this.savedView.viewType;
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
  .layout-menu {
    ._layout-type {
      color: $font-color-secondary;

      &.-disabled {
        color: $font-color-tertiary;
      }
    }

    ._icon {
      margin-left: .4rem;
    }
  }

  ._layout-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: .8rem;
      min-width: 31rem;

      ._layout-button-container {
        position: relative;

        ._plan-tag {
          position: absolute;
          top: -.6rem;
          right: -.4rem;
        }

        ._select-btn {
          height: 5.6rem;
          width: 100%;
        }
      }

  }

</style>
